import "@/App.css";
import React from "react";
import { BrowserRouter, Route, Routes, Navigate } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "sonner";
import LandingPage from "./pages/LandingPage";
import AanmeldPage from "./pages/AanmeldPage";
import BaselinePage from "./pages/BaselinePage";
import GegevensPage from "./pages/GegevensPage";
import BevestigdPage from "./pages/BevestigdPage";
import FollowupPage from "./pages/FollowupPage";
import WenInstructiesPage from "./pages/WenInstructiesPage";
import PrivacyPage from "./pages/PrivacyPage";
import NotFoundPage from "./pages/NotFoundPage";
import { AdminAuthProvider } from "./lib/adminAuth";
import { RequireAdmin } from "./components/admin/RequireAdmin";
import { AdminLayout } from "./components/admin/AdminLayout";
import LoginPage from "./pages/admin/LoginPage";
import MfaChallengePage from "./pages/admin/MfaChallengePage";
import WachtwoordVergetenPage from "./pages/admin/WachtwoordVergetenPage";
import StijlPreviewPage from "./pages/admin/StijlPreviewPage";
import WachtwoordHerstellenPage from "./pages/admin/WachtwoordHerstellenPage";
import DashboardPage from "./pages/admin/DashboardPage";
import WerknemersPage from "./pages/admin/WerknemersPage";
import WerknemerDetailPage from "./pages/admin/WerknemerDetailPage";
import PijnFlagsPage from "./pages/admin/PijnFlagsPage";
import NotificatiesPage from "./pages/admin/NotificatiesPage";
import WerkgeversPage from "./pages/admin/WerkgeversPage";
import WerkgeverDetailPage from "./pages/admin/WerkgeverDetailPage";
import BestellingenPage from "./pages/admin/BestellingenPage";
import GebruikersPage from "./pages/admin/GebruikersPage";
import {
  AanmeetdagenPage, EmailTemplatesPage, AuditLogPage, InstellingenPage,
} from "./pages/admin/StubPages";
import PreviewVragenlijstPage from "./pages/admin/PreviewVragenlijstPage";
import PatronaPage from "./pages/admin/PatronaPage";
import AanmeetdagenIndexPage from "./pages/aanmetingen/AanmeetdagenIndexPage";
import AanmeetdagWerknemersPage from "./pages/aanmetingen/AanmeetdagWerknemersPage";
import WizardPage from "./pages/aanmetingen/WizardPage";

/**
 * Welke voordeur hoort bij dit adres?
 *
 * De app luistert op twee namen (besluit Steven 19-08-2026):
 *   vragenlijst.fulkraworks.nl — wat een deelnemer krijgt: de startpagina met uitleg
 *   app.fulkraworks.nl         — beheer en dashboard: hier hoort meteen een inlogscherm
 *
 * Alleen de startpagina verschilt; alle andere paden blijven op beide namen werken,
 * zodat een aanmeldlink die per ongeluk op het andere adres wordt geopend, niet stukloopt.
 */
function Voordeur() {
  const host = typeof window === "undefined" ? "" : window.location.hostname;
  const isBeheerAdres = host.startsWith("app.");
  return isBeheerAdres ? <Navigate to="/admin" replace /> : <LandingPage />;
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: false, refetchOnWindowFocus: false },
    mutations: { retry: false },
  },
});

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AdminAuthProvider>
          <Toaster position="top-right" richColors closeButton />
          <Routes>
            {/* Werknemer-flow (publiek) */}
            <Route path="/" element={<Voordeur />} />
            <Route path="/aanmeld/:aanmeldcode" element={<AanmeldPage />} />
            <Route path="/gegevens/:progressie_token" element={<GegevensPage />} />
            <Route path="/baseline/:progressie_token" element={<BaselinePage />} />
            <Route path="/baseline/:progressie_token/bevestigd" element={<BevestigdPage kind="baseline" />} />
            <Route path="/followup/:progressie_token" element={<FollowupPage />} />
            <Route path="/followup/:progressie_token/bevestigd" element={<BevestigdPage kind="followup" />} />
            <Route path="/wen-instructies" element={<WenInstructiesPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />

            {/* Admin-routes */}
            <Route path="/admin/login" element={<LoginPage />} />
            <Route path="/admin/mfa" element={<MfaChallengePage />} />
            <Route path="/admin/wachtwoord-vergeten" element={<WachtwoordVergetenPage />} />
            <Route path="/admin/wachtwoord-herstellen" element={<WachtwoordHerstellenPage />} />
            {/* Proefpagina bij de restyling — blok 1. Alleen componenten, geen gegevens. */}
            <Route path="/admin/stijl" element={<StijlPreviewPage />} />
            <Route
              path="/admin"
              element={<RequireAdmin><AdminLayout /></RequireAdmin>}
            >
              <Route index element={<DashboardPage />} />
              <Route path="werkgevers" element={<WerkgeversPage />} />
              <Route path="werkgevers/:id" element={<WerkgeverDetailPage />} />
              <Route path="werknemers" element={<WerknemersPage />} />
              <Route path="werknemers/:id" element={<WerknemerDetailPage />} />
              <Route path="aanmeetdagen" element={<AanmeetdagenPage />} />
              <Route path="bestellingen" element={<BestellingenPage />} />
              <Route path="pijn-flags" element={<PijnFlagsPage />} />
              <Route path="notificaties" element={<NotificatiesPage />} />
              <Route path="email-templates" element={<EmailTemplatesPage />} />
              <Route path="gebruikers" element={<GebruikersPage />} />
              <Route path="audit-log" element={<AuditLogPage />} />
              <Route path="instellingen" element={<InstellingenPage />} />
              <Route path="preview/vragenlijst" element={<PreviewVragenlijstPage />} />
              <Route path="patrona" element={<PatronaPage />} />
              <Route path="*" element={<Navigate to="/admin" replace />} />
            </Route>

            {/* Aanmeetdag-tablet (admin-rol gated) */}
            <Route path="/aanmetingen" element={<RequireAdmin><AanmeetdagenIndexPage /></RequireAdmin>} />
            <Route path="/aanmetingen/dag/:aanmeetdag_id" element={<RequireAdmin><AanmeetdagWerknemersPage /></RequireAdmin>} />
            <Route path="/aanmetingen/dag/:aanmeetdag_id/werknemer/:werknemer_id" element={<RequireAdmin><WizardPage /></RequireAdmin>} />

            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </AdminAuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
